import React from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../app/hooks";
import { FlightDetails, Mapper, RMMealInfo } from "../../../types";
import { selectAirlineLinks } from "../../config/ConfigSlice";
import AirlineLogo from "./AirlineLogo";
import { format, parse } from "date-fns";
import { parseSeatClassOverrides, getClassLabel } from "../utils";
import { selectAppConfig } from "../../config/ConfigSlice";

function SearchFlightsResultModalFlight({
  flightDetails,
  mapper,
  locale,
}: {
  flightDetails: FlightDetails;
  mapper: Mapper;
  locale: globalThis.Locale;
}) {
  const { t } = useTranslation();
  const enableAirlineLink = useAppSelector(
    (state) => state.config.components.Results.enableAirlineLink
  );
  const showFirstClass = useAppSelector(
    (state) => state.config.components.Results.showFirstClass
  );
  const translateSeatClass = useAppSelector(
    (state) => state.config.components.Results.translateSeatClass
  );
  const translateSeatClassNew = useAppSelector(
    (state) => state.config.components.Results.translateSeatClassNew
  );
  const airlineLinks = useAppSelector(selectAirlineLinks);
  const seatClassOverrides = parseSeatClassOverrides(translateSeatClass);
  const seatClassOverridesNew = parseSeatClassOverrides(translateSeatClassNew);
  const generateAirlineLabel = (airlineLabel, airlineCode) => {
    const airlineLink = mapper.includeAirlineLinks
      ? airlineLinks.find((airline) => airline.code === airlineCode)
      : null;
    if (airlineLink) {
      return (
        <a
          href={`//${airlineLink.url}`}
          target="_blank"
          title={airlineLink.url}
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          {airlineLabel}
        </a>
      );
    } else {
      return airlineLabel;
    }
  };
  if (flightDetails === null) {
    return null;
  }
  const excludedCustomerConfig = {
    ja: ["ANA-dom", "ANA-dom-ja", "ANA-intl", "ANA-intl-ja"],
  };
  const localeCode = locale?.code || "en";
  const customerId = useAppSelector(
    (state) => selectAppConfig(state).customerId
  );

  const shouldBlockAirlineLink = (localeCode, customerId) => {
    return excludedCustomerConfig[localeCode]?.includes(customerId);
  };
  return (
    <table className="table is-bordered is-size-7 is-fullwidth">
      <thead>
        <tr>
          <th colSpan={2}>
            <div className="columns is-vcentered is-mobile">
              <div className="column is-narrow">
                <AirlineLogo airlineCode={flightDetails.operatedBy} />
              </div>
              <div className="column">
                <p className="is-size-5">
                  {enableAirlineLink &&
                  !shouldBlockAirlineLink(localeCode, customerId)
                    ? generateAirlineLabel(
                        flightDetails.airline,
                        flightDetails.operatedBy
                      )
                    : !shouldBlockAirlineLink(localeCode, customerId)
                    ? flightDetails.airline
                    : null}
                  {!shouldBlockAirlineLink(localeCode, customerId) &&
                  flightDetails.airline
                    ? ` - `
                    : ``}
                  {`${flightDetails.operatedBy}${flightDetails.flightNo}`}
                </p>
                {flightDetails.sadName && (
                  <p className="is-size-7">
                    {`${t("LABEL_OPERATED_BY")} ${flightDetails.sadName} - ${
                      flightDetails.oagOpByCarrier
                    }${flightDetails.oagOpByFlightNo}`}
                  </p>
                )}
              </div>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style={{ width: "30%" }}>{t("LABEL_FLIGHT_FINDER_FROM")}</td>
          <td>
            {flightDetails.departPortName}{" "}
            {flightDetails.departureTerminal &&
              (locale?.code === "ja"
                ? `(${flightDetails.departureTerminal.replace(
                    "Terminal",
                    "ターミナル"
                  )})`
                : `(${flightDetails.departureTerminal})`)}
          </td>
        </tr>
        <tr>
          <td>{t("RADIO_SORTBY_DEPARTURE_TIME")}</td>
          <td>
            {flightDetails.departureTimeAsString} -{" "}
            {locale?.code === "ar-SA"
              ? format(
                  parse(
                    flightDetails.departureDateAsString,
                    "d MMMM yyyy",
                    new Date()
                  ),
                  "dd MMMM yyyy",
                  { locale: locale }
                )
              : locale?.code === "ja"
              ? format(
                  parse(
                    flightDetails.departureDateAsString,
                    "d MMMM yyyy",
                    new Date()
                  ),
                  "yyyy年M月d日",
                  { locale: locale }
                )
              : flightDetails.departureDateAsString}
          </td>
        </tr>
        <tr>
          <td>{t("LABEL_FLIGHT_FINDER_TO")}</td>
          <td>
            {flightDetails.arrivalPortName}{" "}
            {flightDetails.arrivalTerminal &&
              (locale?.code === "ja"
                ? `(${flightDetails.arrivalTerminal.replace(
                    "Terminal",
                    "ターミナル"
                  )})`
                : `(${flightDetails.arrivalTerminal})`)}
          </td>
        </tr>
        <tr>
          <td>{t("RADIO_SORTBY_ARRIVAL_TIME")}</td>
          <td>
            {flightDetails.arrivalTimeAsString} -{" "}
            {locale?.code === "ar-SA"
              ? format(
                  parse(
                    flightDetails.arrivalDateAsString,
                    "d MMMM yyyy",
                    new Date()
                  ),
                  "dd MMMM yyyy",
                  { locale: locale }
                )
              : locale?.code === "ja"
              ? format(
                  parse(
                    flightDetails.arrivalDateAsString,
                    "d MMMM yyyy",
                    new Date()
                  ),
                  "yyyy年M月d日",
                  { locale: locale }
                )
              : flightDetails.arrivalDateAsString}
          </td>
        </tr>
        <tr>
          <td>{t("RADIO_SORTBY_DURATION")}</td>
          <td>
            {locale?.code === "ja"
              ? `${Math.floor(flightDetails.travelTime / 60)}時間 ${
                  flightDetails.travelTime % 60
                }分`
              : `${Math.floor(flightDetails.travelTime / 60)}h ${
                  flightDetails.travelTime % 60
                }m`}
          </td>
        </tr>
        <tr>
          <td>{t("LABEL_FLIGHT_FINDER_CLASSES")}</td>
          <td>
            <ul>
              {showFirstClass && flightDetails.classFirst !== null && (
                <li>
                  {getClassLabel(
                    "CLASS_F",
                    flightDetails.departureDate,
                    mapper.bookingLink.transitionDate,
                    seatClassOverrides,
                    seatClassOverridesNew,
                    t
                  )}
                </li>
              )}
              {flightDetails.classBusiness !== null && <li>{t("CLASS_B")}</li>}
              {flightDetails.classEconomy !== null && (
                <li>
                  {getClassLabel(
                    "CLASS_E",
                    flightDetails.departureDate,
                    mapper.bookingLink.transitionDate,
                    seatClassOverrides,
                    seatClassOverridesNew,
                    t
                  )}
                </li>
              )}
            </ul>
          </td>
        </tr>
        <Meals list={flightDetails.mealsFirst} label={t("MEAL_INFO_FIRST")} />
        <Meals
          list={flightDetails.mealsBusiness}
          label={t("MEAL_INFO_BUSINESS")}
        />
        <Meals
          list={flightDetails.mealsEconomy}
          label={t("MEAL_INFO_ECONOMY")}
        />
        <tr>
          <td>{t("LABEL_FLIGHT_FINDER_AIRCRAFT")}</td>
          <td>{flightDetails.aircraftName}</td>
        </tr>
      </tbody>
    </table>
  );
}

function Meals({ list, label }: { list: RMMealInfo[]; label: string }) {
  const { t } = useTranslation();

  if (list && list.length > 0) {
    return (
      <tr>
        <td>{label}</td>
        <td>
          <ul>
            {list.map((meal, i) => (
              <li key={i}>{t("MEAL_INFO_CODE_" + meal.code)}</li>
            ))}
          </ul>
        </td>
      </tr>
    );
  } else {
    return null;
  }
}

export default SearchFlightsResultModalFlight;
